import { BASE_URLS } from '../../../config/base-urls';
import { createApi } from '../../../config/create-api';
import { makeError } from '../../../config/research/v1';
import { STALE_TIME } from '../../../config/stale-time';
import { getStudioHeaders } from '../config/headers';

import {
    GetExecutionRequest,
    GetExecutionResponse,
    GetExecutionResponseV2,
    GetExecutionsRequest,
    GetExecutionsResponse,
    PostExecuteQueryRequestV2,
    PostExecuteQueryResponseV2,
    STUDIO_EXECUTION_ENDPOINTS,
    STUDIO_EXECUTION_V2_ENDPOINTS,
} from './models';
import { mapExecutionParamsV2ToV1, mapExecutionV2ToV1 } from './utils';

const studioExecutionApi = createApi(
    build => ({
        get: {
            /** @todo migrate to v2 request/response */
            execution: build<GetExecutionResponse, GetExecutionRequest>({
                cachePrefix: 'execution-by-id',
                queryFn: async ({ id, limit = 50 * 1000, page = 1, filter, order, proxyUri }, { baseFetch }) => {
                    try {
                        const baseUrl = proxyUri ? BASE_URLS['same-origin'] : BASE_URLS['research-api-v2'];
                        const path = proxyUri ?? STUDIO_EXECUTION_V2_ENDPOINTS.GET.EXECUTION_ROWS({ id });

                        const search = mapExecutionParamsV2ToV1({ limit, page, order, filter });

                        const res = await baseFetch<false, any, GetExecutionResponseV2>({
                            url: `${baseUrl}${path}${search}`,
                        });

                        if (!res.data) {
                            return makeError('Execution data not found.');
                        }

                        /** return res, the proxy calls this fetcher which runs the mapper for us */
                        if (baseUrl === BASE_URLS['same-origin']) {
                            return res as any;
                        }
                        return mapExecutionV2ToV1(res);
                    } catch (error) {
                        const err = error as Error;
                        console.error(err);

                        return makeError('Failed to fetch execution.');
                    }
                },
            }),
            executionV2: build<GetExecutionResponse, GetExecutionRequest>({
                cachePrefix: 'execution-by-visualization-id',
                queryFn: async ({ id, limit = 50 * 1000, page = 1, filter, order }, { baseFetch }) => {
                    try {
                        const search = mapExecutionParamsV2ToV1({ limit, page, order, filter });

                        const { baseUrl, path } = {
                            baseUrl: BASE_URLS['research-api-v2'],
                            path: STUDIO_EXECUTION_V2_ENDPOINTS.GET.EXECUTION_ROWS,
                        };
                        const res = await baseFetch<false, any, GetExecutionResponseV2>({
                            url: `${baseUrl}${path({ id })}${search}`,
                        });

                        if (!res.data) {
                            return makeError('Execution not found.');
                        }

                        return mapExecutionV2ToV1(res);
                    } catch (error) {
                        const err = error as Error;
                        console.error(err);

                        return makeError('Failed to fetch execution.');
                    }
                },
            }),
            /** uses v1 API. v2 BE doesn't have this endpoint yet */
            executions: build<GetExecutionsResponse, GetExecutionsRequest>({
                cachePrefix: 'all-executions',
                query: ({ queryId, limit = 5, page = 1, state, ...rest }) => {
                    return {
                        uri: STUDIO_EXECUTION_ENDPOINTS.GET.EXECUTIONS(),
                        params: {
                            queryId,
                            limit,
                            page,
                            state,
                            ...rest,
                        },
                        options: {
                            headers: getStudioHeaders(),
                        },
                    };
                },
            }),
        },
        post: {
            executeQuery: build<PostExecuteQueryResponseV2, PostExecuteQueryRequestV2>({
                cachePrefix: 'run-execution-by-id',
                baseUrl: 'research-api-v2',
                query: ({ query_id }) => {
                    return {
                        uri: STUDIO_EXECUTION_V2_ENDPOINTS.POST.EXECUTE_QUERY(),
                        options: {
                            method: build.Method.Post,
                            body: { query_id },
                        },
                    };
                },
            }),
        },
    }),
    {
        useGetExecution: {
            staleTime: STALE_TIME.$1Minute,
            models: {
                DEFAULT_TABLE_EXECUTION_LIMIT: 25,
            },
        },
    },
);

export { studioExecutionApi };
